import { inject, Injectable } from "@angular/core"
import { FireStoreService } from "@services/fire-store.service"
import { SubSectionService } from "@services/sub-section.service"
import { ProgramPubDTO, ProgramPublishedService, SectionPubDTO } from "@services/program.published.service"

export interface SectionDTO {
  removing?: boolean // front only
  id?: string
  title: string
  title_internal: string

  subsectionsRefs: Array<{
    id: string
    versionId: string
  }>
}

export interface ThemingDTO {
  logoSrc: string
  footerSrc: string
  bgColor: string
  textColor: string
  menuBgColor: string
}

export interface ProgramDTO {
  id?: string
  alias: string
  version: string

  email: {
    subject: string
    content: string
    alias: string
  }
  disabledMessage: string
  name: string
  town: string
  date: string
  accessible: boolean
  introductionId: string
  introductionVersionId: string

  theming?: ThemingDTO
  sections: Array<SectionDTO>
}

@Injectable({
  providedIn: "root",
})
export class ProgramService extends FireStoreService<ProgramDTO> {
  subSectionService = inject(SubSectionService)
  programPublishedService = inject(ProgramPublishedService)

  constructor() {
    super()
    this.init({
      collectionKey: `program`,
      fullTextFields: [],
    })
  }

  override beforeSavingHook(program: ProgramDTO): ProgramDTO {
    program?.sections?.forEach((section) => {
      if (!section.id || section.id.startsWith("RANDOM")) {
        section.id = this.uuid()
      }
    })
    if (program.alias == null && program.id) program.alias = this.idToAlias(program.id || "")
    return program
  }

  /**
   * Picks 4 characters at random positions from within the original Firestore ID.
   * This is synchronous and non-deterministic: calling it twice on the same ID
   * can produce different aliases.
   */
  idToAlias(id: string, length = 5): string {
    let alias = ""
    for (let i = 0; i < length; i++) {
      const index = Math.floor(Math.random() * id.length)
      alias += id[index]
    }
    return alias
  }

  async convertSection(section: SectionDTO): Promise<SectionPubDTO> {
    //@ts-ignore
    const subSections = await Promise.all(
      section.subsectionsRefs?.map(async (ref) => {
        const sub = await this.subSectionService.get(ref.id)
        const version = sub.versions?.find((v) => v.id === ref.versionId)
        return {
          id: sub.id || "",
          title: version?.title || "",
          content: version?.content || "",
          backgroundImg: version?.backgroundImg || "",
          hideTitleSection: version?.hideTitleSection || false,
        }
      })
    )
    //@ts-ignore
    return {
      id: section.id || "",
      title: section.title || "",
      subSections: subSections || [],
    }
  }

  async publish(programId: string): Promise<any> {
    try {
      const program = await this.get(programId)
      if (!program) return
      let intro = {} as any
      if (program.introductionId) {
        const sub = await this.subSectionService.get(program.introductionId)
        const version = sub?.versions?.find((s) => s.id === program.introductionVersionId)
        if (version) intro = version
      }

      const theming = program.theming
      const programPublished: ProgramPubDTO = {
        id: programId,
        name: program.name,
        date: program.date,
        town: program.town,
        version: "1111",
        intro,
        sections: [],
        date_published: new Date(),
        accessible: program.accessible,
        disabledMessage: program.disabledMessage || "",

        theming: {
          bgColor: theming?.bgColor || "",
          footerSrc: theming?.footerSrc || "",
          logoSrc: theming?.logoSrc || "",
          menuBgColor: theming?.menuBgColor || "",
          textColor: theming?.textColor || "",
        },
      }
      let sections: Array<SectionPubDTO> = []
      if (program?.sections) {
        //@ts-ignore
        sections = await Promise.all(program?.sections.map(async (section) => this.convertSection(section)))
      }

      const programAlreadyPublished = await this.programPublishedService.get(programId)
      if (programAlreadyPublished) {
        await this.programPublishedService.update(programPublished)
      } else {
        await this.programPublishedService.create(programPublished, programId)
      }
      await this.programPublishedService.addSections(programId, sections)
      // await this.programPublishedService.saveCurrentProgramId(programId)
    } catch (e) {
      this.handleError(e, "")
    }
  }

  async duplicateProgram(programId: string, newName: string): Promise<any> {
    const program = await this.get(programId)
    if (!program) throw new Error("Unable to duplicate")

    const { id, ...newProgram } = program
    const prog = await this.create({
      ...newProgram,
      name: newName,
    })
    await this.publish(prog?.id || "")
    this.notifService.displaySuccess("Le programme a été dupliqué.")
  }
}
